import * as Sentry from "@sentry/react";
import { toast } from "@/hooks/use-toast";

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  metadata?: Record<string, any>;
  errorCode?: string;
}

export class AppError extends Error {
  public readonly context: ErrorContext;
  public readonly severity: 'low' | 'medium' | 'high' | 'critical';
  public readonly userFriendly: boolean;

  constructor(
    message: string,
    context: ErrorContext = {},
    severity: 'low' | 'medium' | 'high' | 'critical' = 'medium',
    userFriendly = false
  ) {
    super(message);
    this.name = 'AppError';
    this.context = context;
    this.severity = severity;
    this.userFriendly = userFriendly;
  }
}

export const errorHandler = {
  captureError(error: Error | AppError, context: ErrorContext = {}) {
    const isAppError = error instanceof AppError;
    const severity = isAppError ? error.severity : 'medium';
    
    Sentry.withScope((scope) => {
      scope.setTag('severity', severity);
      scope.setLevel(this.mapSeverityToSentryLevel(severity));
      
      if (context.component) scope.setTag('component', context.component);
      if (context.action) scope.setTag('action', context.action);
      if (context.userId) scope.setUser({ id: context.userId });
      if (context.metadata) scope.setContext('metadata', context.metadata);
      
      Sentry.captureException(error);
    });

    if (import.meta.env.DEV) {
      console.error('Error captured:', {
        error,
        context,
        severity,
        stack: error.stack,
      });
    }

    if (isAppError && error.userFriendly) {
      toast({
        variant: severity === 'critical' ? 'destructive' : 'default',
        title: severity === 'critical' ? 'Critical Error' : 'Something went wrong',
        description: error.message,
      });
    } else if (severity === 'critical') {
      toast({
        variant: 'destructive',
        title: 'System Error',
        description: 'A critical error occurred. Our team has been notified.',
      });
    }
  },

  handleApiError(error: any, context: ErrorContext = {}) {
    if (error?.code === 'PGRST301') {
      this.captureError(
        new AppError(
          'Access denied. Please check your permissions.',
          { ...context, errorCode: 'RLS_VIOLATION' },
          'high',
          true
        )
      );
    } else if (error?.code === 'PGRST116') {
      this.captureError(
        new AppError(
          'Requested data not found.',
          { ...context, errorCode: 'NOT_FOUND' },
          'low',
          true
        )
      );
    } else if (error?.message?.includes('fetch')) {
      this.captureError(
        new AppError(
          'Network connection error. Please check your internet connection.',
          { ...context, errorCode: 'NETWORK_ERROR' },
          'medium',
          true
        )
      );
    } else {
      this.captureError(
        new AppError(
          error?.message || 'An unexpected error occurred.',
          { ...context, errorCode: 'API_ERROR' },
          'medium'
        )
      );
    }
  },

  handleAuthError(error: any, context: ErrorContext = {}) {
    const authContext = { ...context, component: 'Auth' };
    
    if (error?.message?.includes('Invalid login credentials')) {
      this.captureError(
        new AppError(
          'Invalid email or password.',
          authContext,
          'low',
          true
        )
      );
    } else if (error?.message?.includes('Email not confirmed')) {
      this.captureError(
        new AppError(
          'Please verify your email address before signing in.',
          authContext,
          'low',
          true
        )
      );
    } else {
      this.captureError(
        new AppError(
          error?.message || 'Authentication failed.',
          authContext,
          'medium',
          true
        )
      );
    }
  },

  mapSeverityToSentryLevel(severity: 'low' | 'medium' | 'high' | 'critical') {
    switch (severity) {
      case 'low': return 'info';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'fatal';
      default: return 'error';
    }
  },

  startTransaction(name: string, operation: string) {
    return Sentry.startSpan({ name, op: operation }, () => {});
  },

  addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: 'info',
    });
  },
};

// Simple error boundary without JSX
export const AppErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => children,
  {
    fallback: () => null,
    beforeCapture: (scope) => {
      scope.setTag('errorBoundary', true);
      scope.setLevel('fatal');
    },
  }
);